/**
 * LocalIdAllocatorService
 *
 * Generates local-only IDs for entities that haven't yet been persisted to the backend. Used in offline and
 * impersonation modes where the backend cannot be reached for creates.
 *
 * ID Formats:
 *
 * - Assessment SQL IDs: negative integers (e.g. -1, -2, -3)
 * - FHIR resource GUIDs: 'local-{uuid}' prefix (e.g. 'local-a1b2c3d4-...')
 *
 * Discriminator rule (used by sync engine): isLocal(id) === true → POST (create, server assigns real ID) isLocal(id)
 * === false → PUT (update, use existing server ID)
 */
import { Injectable, signal } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class LocalIdAllocatorService {
  /** Monotonically decreasing counter for negative integer assessment IDs. */
  private readonly counter = signal(0);

  /**
   * Returns the next negative integer for a locally-created assessment SQL ID. Each call returns a unique value (-1,
   * -2, -3, …).
   */
  nextAssessmentId(): number {
    this.counter.update((c) => c - 1);
    return this.counter();
  }

  /**
   * Returns a locally-scoped FHIR resource ID in the form 'local-{uuid}'. Used for QuestionnaireResponse and
   * Questionnaire created while offline.
   */
  nextFhirId(): string {
    return `local-${crypto.randomUUID()}`;
  }

  /**
   * Returns true when the given ID is a locally-generated placeholder that has not yet been reconciled against a real
   * server ID.
   */
  isLocal(id: string | number | undefined | null): boolean {
    if (id == null) return false;
    if (typeof id === 'number') return id < 0;
    return typeof id === 'string' && id.startsWith('local-');
  }
}
